import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import itemLogo from '../assets/icons/icon-gas-truck.svg';

import './fuelEstimateCard.css';

const CATEGORY = 'GAS';

// Componente de Vehículo que se puede arrastrar
const VehicleItem = ({ index, vehicle, fuelPrice, indexWidth }) => {
    const [, ref] = useDrag({
        type: CATEGORY,
        item: { vehicle },
    });

    const renderDataItem = (value, previousValue, formatter, unit) => (
        <span className={`${value > previousValue ? 'high' : (value < previousValue ? 'low' : 'normal')}`} style={{ textAlign: 'center', width: '150px', whiteSpace: 'nowrap' }}>
            {formatter(value)} {unit}
        </span>
    );

    return (
        <div ref={ref} className='itemTop_container' style={{ cursor: 'grab' }}>
            <span className='itemTop_index' style={{ '--item-index-width': indexWidth }}>{index}</span>
            <img src={itemLogo} alt='Ícono del vehículo' className='itemTop_logo' />
            <span className='itemTop_name'>{vehicle.label}</span>
            <div className='itemTop_data_container'>
                {renderDataItem(vehicle.currentConsumed, vehicle.previousConsumed, val => val.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'L')}
                {renderDataItem(vehicle.currentConsumed * fuelPrice, vehicle.previousConsumed * fuelPrice, val => '$ ' + val.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'MXN')}
            </div>
            <div className='itemTop_data_container'>
                {renderDataItem(vehicle.previousConsumed, vehicle.currentConsumed, val => val.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'L')}
                {renderDataItem(vehicle.previousConsumed * fuelPrice, vehicle.currentConsumed * fuelPrice, val => '$ ' + val.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'MXN')}
            </div>
        </div>
    );
};

// Contenedor de columna para los vehículos de un tipo específico
const VehicleColumn = ({ vehicles, fuelPrice, onDrop }) => {
    const [isOver, setIsOver] = useState(false);

    const [{ canDrop, isOverCurrent }, ref] = useDrop({
        accept: CATEGORY,
        drop: (item) => onDrop(item.vehicle),
        collect: (monitor) => ({
            isOverCurrent: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
        hover: () => setIsOver(true),
        leave: () => setIsOver(false),
    });

    useEffect(() => {
        setIsOver(isOverCurrent); // Actualizar estado según monitor
    }, [isOverCurrent]);

    const indexWidth = vehicles.length < 10 ? '2ch' : vehicles.length < 100 ? '3ch' : '4ch';

    return (
        <div
            ref={ref}
            className='vehicle_container'
        >
            <div className={`${isOver ? 'hovered' : ''}`}>
                {vehicles.length === 0 ? (
                    <span className='text_no_vehicles'>Ningún vehículo agregado</span>
                ) : (
                    vehicles.map((vehicle, index) => (
                        <VehicleItem key={vehicle.label} index={index + 1} vehicle={vehicle} fuelPrice={fuelPrice} indexWidth={indexWidth} />
                    ))
                )}
            </div>
        </div>
    );
};

// Componente principal de la tarjeta de estimación de combustible perdido
const FuelEstimateCard = ({ devices, displayedTopFuelEfficiency, showAllTopFuelEfficiency, userConstants, updateUserConstants }) => {
    const [columns, setColumns] = useState({ magna: userConstants.magnaDevices, premium: userConstants.premiumDevices, diesel: userConstants.dieselDevices });

    const [priceMagna, setPriceMagna] = useState(userConstants.magnaPrice);
    const [pricePremium, setPricePremium] = useState(userConstants.premiumPrice);
    const [priceDiesel, setPriceDiesel] = useState(userConstants.dieselPrice);

    const sortVehicles = (vehicles, pricePerFuel) => {
        return vehicles.sort((a, b) => {
            const aPricePerFuel = a.currentConsumed * pricePerFuel;
            const bPricePerFuel = b.currentConsumed * pricePerFuel;
            if (bPricePerFuel === aPricePerFuel) {
                return b.previousConsumed * pricePerFuel - a.previousConsumed * pricePerFuel;
            }
            return bPricePerFuel - aPricePerFuel;
        });
    };

    // Actualizar columnas al cambiar `displayedTopRalentiFuel`
    useEffect(() => {
        const currentCategoryMap = {};
        Object.keys(columns).forEach((category) => {
            columns[category].forEach((vehicle) => {
                currentCategoryMap[vehicle.label] = category;
            });
        });

        // Filtramos dispositivos visibles
        const visibleDevices = devices.filter(device => device.visible);

        const updatedColumns = visibleDevices.reduce(
            (acc, device) => {
                const fuelData = displayedTopFuelEfficiency.find((item) => item.car === device.label);
                const updatedDevice = {
                    ...device,
                    currentConsumed: fuelData?.currentConsumed || 0,
                    previousConsumed: fuelData?.previousConsumed || 0,
                };

                const category = currentCategoryMap[device.label] || device.fuelType;
                acc[category] = [...(acc[category] || []), updatedDevice];
                return acc;
            },
            { magna: [], premium: [], diesel: [] }
        );

        Object.keys(updatedColumns).forEach((type) => {
            const pricePerFuel = type === 'magna' ? priceMagna : type === 'premium' ? pricePremium : priceDiesel;
            updatedColumns[type] = sortVehicles(updatedColumns[type], pricePerFuel);
        });

        updateUserConstants({magnaDevices: updatedColumns.magna, premiumDevices: updatedColumns.premium, dieselDevices: updatedColumns.diesel });
        setColumns(updatedColumns);
    }, [devices, displayedTopFuelEfficiency]);

    // Obtener vehículos recortados si `showAllTopFuelEfficiency` es true
    const getSortedVehicles = (vehicles) => {
        return showAllTopFuelEfficiency ? vehicles : vehicles.slice(0, 5);
    };

    const moveVehicle = (vehicle, from, to) => {
        const fuelPrice =
            to === 'magna' ? priceMagna
                : to === 'premium' ? pricePremium
                    : priceDiesel;

        setColumns((prev) => {
            const newColumns = { ...prev };
            newColumns[from] = newColumns[from].filter((v) => v.label !== vehicle.label);
            newColumns[to] = sortVehicles([...newColumns[to], vehicle], fuelPrice);
            updateUserConstants({magnaDevices: newColumns.magna, premiumDevices: newColumns.premium, dieselDevices: newColumns.diesel });
            return newColumns;
        });
    };

    const handleDrop = (vehicle, to) => {
        const from = Object.keys(columns).find(col => columns[col].some(v => v.label === vehicle.label));
        if (from && from !== to) moveVehicle(vehicle, from, to);
    };

    const handleInputChange = (e, setPriceFunction, defaultValue, type) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value >= 0) {
            updateUserConstants({[type]: value});
            setPriceFunction(value);
        } else {
            updateUserConstants({[type]: defaultValue});
            setPriceFunction(defaultValue); // Valor predeterminado si el input no es válido
        }
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className='fuel-estimate-card'>
                <div className='vehicle-columns'>
                    <div className='vehicle-column'>
                        <span>Gasolina Magna</span>
                        <div className='increment_input_container'>
                            <label>
                                Precio estimado:
                            </label>
                            <div>
                                <input
                                    type='number'
                                    value={priceMagna}
                                    min='0.1'
                                    step='0.1'
                                    onChange={(e) => handleInputChange(e, setPriceMagna, 0, 'magnaPrice')}
                                />
                                <span>
                                    MXN
                                </span>
                            </div>
                        </div>
                        <VehicleColumn
                            vehicles={getSortedVehicles(columns.magna)}
                            fuelPrice={priceMagna}
                            onDrop={(vehicle) => handleDrop(vehicle, 'magna')}
                        />
                    </div>
                    <div className='vehicle-column'>
                        <span>Gasolina Premium</span>
                        <div className='increment_input_container'>
                            <label>
                                Precio estimado:
                            </label>
                            <div>
                                <input
                                    type='number'
                                    value={pricePremium}
                                    min='0.1'
                                    step='0.1'
                                    onChange={(e) => handleInputChange(e, setPricePremium, 0, 'premiumPrice')}
                                />
                                <span>
                                    MXN
                                </span>
                            </div>
                        </div>
                        <VehicleColumn
                            vehicles={getSortedVehicles(columns.premium)}
                            fuelPrice={pricePremium}
                            onDrop={(vehicle) => handleDrop(vehicle, 'premium')}
                        />
                    </div>
                    <div className='vehicle-column'>
                        <span>Diesel</span>
                        <div className='increment_input_container'>
                            <label>
                                Precio estimado:
                            </label>
                            <div>
                                <input
                                    type='number'
                                    value={priceDiesel}
                                    min='0.1'
                                    step='0.1'
                                    onChange={(e) => handleInputChange(e, setPriceDiesel, 0, 'dieselPrice')}
                                />
                                <span>
                                    MXN
                                </span>
                            </div>
                        </div>
                        <VehicleColumn
                            vehicles={getSortedVehicles(columns.diesel)}
                            fuelPrice={priceDiesel}
                            onDrop={(vehicle) => handleDrop(vehicle, 'diesel')}
                        />
                    </div>
                </div>
            </div>
        </DndProvider>
    );
};

export default FuelEstimateCard;
